/**
 * Deposits and withdrawals.
 *
 * The deposit half leans on something this product already has and most
 * exchanges do not: the user is signed in *with the wallet they are paying
 * from*, so instead of copying an address into another app they can send the
 * transfer from here in one click. The address is still shown, because a
 * hardware wallet or a phone will always need it.
 *
 * The withdrawal half has no destination field. Funds return to the signed-in
 * address, which is why an attacker holding a stolen session cannot reroute
 * them — and the page says so plainly rather than leaving it implied.
 *
 * Both live in modals rather than as permanent panels: most visits to this
 * page are to check history, not to move money, and a modal keeps that the
 * default view instead of two big forms sitting open every time.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { erc20Abi, parseEther, parseUnits } from 'viem'
import {
  useAccount,
  useSendTransaction,
  useSwitchChain,
  useWaitForTransactionReceipt,
  useWriteContract,
} from 'wagmi'

import { EmptyState, Modal, SectionHeading, Skeleton, Stat } from '../components/ui'
import { useCancelWithdrawal, useRequestWithdrawal, useWallet } from '../hooks/queries'
import { useAuth } from '../hooks/useAuth'
import { ApiError, type DepositRow, type WalletResponse, type WithdrawalRow } from '../lib/api'
import { cents, shortAddress, timeAgo } from '../lib/format'

type Live = Extract<WalletResponse, { enabled: true }>
type DepositChain = Live['depositChains'][number]
type DepositAsset = DepositChain['assets'][number]

export function WalletPage() {
  const { user, connectAndSignIn } = useAuth()
  const { data, isLoading } = useWallet(Boolean(user))
  const [modal, setModal] = useState<'deposit' | 'withdraw' | null>(null)

  if (!user) {
    return (
      <div className="py-10">
        <EmptyState
          title="Sign in to deposit or withdraw"
          body="Your balance belongs to your wallet, so moving money needs a signed-in session."
          action={
            <button className="btn btn-primary btn-sm" onClick={() => void connectAndSignIn()}>
              Connect wallet
            </button>
          }
        />
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-4 py-6">
        <Skeleton className="h-32" />
        <Skeleton className="h-72" />
      </div>
    )
  }

  if (!data.enabled) {
    return (
      <div className="py-10">
        <EmptyState
          title="Real deposits are not switched on"
          body="This deployment runs on a simulated balance. Nothing here buys, sells or custodies a real token, so there is nothing to deposit or withdraw."
          action={
            <Link to="/packs" className="btn btn-primary btn-sm">
              Browse packs
            </Link>
          }
        />
      </div>
    )
  }

  // Named per chain, because "USDC, USDT, ETH" alone would not say that one of
  // those ETHs is on a different network with a different wait.
  const assetList = data.depositChains
    .map((c) => `${c.assets.map((a) => a.symbol).join('/')} on ${c.chainName}`)
    .join(', ')

  return (
    <div className="py-6">
      <SectionHeading
        title="Wallet"
        sub={`Deposit ${assetList}. Withdrawals always pay out in ${data.token.symbol} on ${data.chain.name}.`}
      />

      {/* The balance and the two actions on it share one panel rather than a
          stat row plus small buttons off in the header — moving money is the
          reason anyone comes to this page, not an afterthought next to the
          title. */}
      <div className="panel flex flex-wrap items-center justify-between gap-6 p-6 sm:p-8">
        <div>
          <div className="eyebrow">Available</div>
          <div className="tnum mt-1 text-4xl font-bold sm:text-5xl">{cents(data.availableCents)}</div>
          <div className="mt-2 text-xs" style={{ color: 'var(--color-faint)' }}>
            {cents(data.limits.dailyRemainingCents)} left to withdraw today
          </div>
        </div>
        <div className="flex w-full gap-3 sm:w-auto">
          <button
            className="btn btn-primary btn-lg flex-1 sm:flex-none sm:px-10"
            onClick={() => setModal('deposit')}
          >
            Deposit
          </button>
          <button
            className="btn btn-ghost btn-lg flex-1 sm:flex-none sm:px-10"
            onClick={() => setModal('withdraw')}
          >
            Withdraw
          </button>
        </div>
      </div>

      <div className="mt-10 grid gap-5 lg:grid-cols-2 lg:items-start">
        <DepositHistory rows={data.deposits} />
        <WithdrawalHistory rows={data.withdrawals} />
      </div>

      {modal === 'deposit' && (
        <Modal title="Deposit" onClose={() => setModal(null)}>
          <Deposit wallet={data} />
        </Modal>
      )}
      {modal === 'withdraw' && (
        <Modal title="Withdraw" onClose={() => setModal(null)}>
          <Withdraw wallet={data} />
        </Modal>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Deposit
 * ------------------------------------------------------------------ */

/**
 * A reverted contract call's useful text is `error.shortMessage`, not
 * `error.message` — viem builds `message` by appending a docs link and
 * version footer, but for a revert it packs the actual reason as a *second*
 * line inside `shortMessage` itself (e.g. `…reverted with the following
 * reason:\nERC20: transfer amount exceeds balance`). Taking just the first
 * line of `message` — the previous approach — showed the generic preamble
 * and silently dropped the one line that says why it failed.
 */
function depositErrorMessage(error: Error): string {
  const short =
    'shortMessage' in error && typeof (error as { shortMessage?: unknown }).shortMessage === 'string'
      ? (error as { shortMessage: string }).shortMessage
      : error.message
  return short.replace(/\n+/g, ' ')
}

function Deposit({ wallet }: { wallet: Live }) {
  const { address, isConnected, chainId: walletChainId } = useAccount()
  const { switchChain, isPending: switching } = useSwitchChain()

  const [chain, setChain] = useState<DepositChain>(wallet.depositChains[0]!)
  const [selected, setSelected] = useState<DepositAsset>(wallet.depositChains[0]!.assets[0]!)
  const [amount, setAmount] = useState('')
  const [copied, setCopied] = useState(false)

  const isNative = selected.address === null
  /* The treasury is the same address on every chain, so a deposit meant for one
     network that is sent from a wallet pointed at another still *arrives* — it
     just arrives somewhere else, and gets credited by that chain's scanner
     instead. Not a loss, but not what was asked for either, so the send is
     gated on the wallet actually being on the chosen chain. */
  const onRightChain = walletChainId === chain.chainId

  // Two hooks, always both called — which one is "live" depends on the asset
  // selector, not on which hook fires. wagmi has no single hook that covers
  // both an ERC-20 transfer and a native send.
  const erc20 = useWriteContract()
  const native = useSendTransaction()

  const hash = isNative ? native.data : erc20.data
  const isPending = isNative ? native.isPending : erc20.isPending
  const error = isNative ? native.error : erc20.error
  const receipt = useWaitForTransactionReceipt({ hash })

  const reset = () => {
    erc20.reset()
    native.reset()
  }

  const parsed = Number(amount)
  // The USD-minimum check only means something for a stablecoin: USDC/USDT
  // are ~1:1 with the amount typed, but ETH has no fixed dollar value on the
  // client, so there is no price here to check it against. A too-small ETH
  // send still lands safely — the backend records it without crediting it,
  // same as any other sub-minimum deposit — it just is not caught here first.
  // The USD-minimum check only means something for a stable asset: USDC/USDT
  // are ~1:1 with the amount typed, but a priced asset has no dollar value on
  // the client, so there is no rate here to check it against. A too-small send
  // still lands safely — the backend records it without crediting it, same as
  // any other sub-minimum deposit — it just is not caught here first.
  const valid =
    Number.isFinite(parsed) &&
    parsed > 0 &&
    (selected.pricing === 'priced' || parsed * 100 >= wallet.limits.minDepositCents)

  const pickChain = (next: DepositChain) => {
    setChain(next)
    setSelected(next.assets[0]!)
    setAmount('')
    reset()
  }

  const send = () => {
    if (!valid || !onRightChain) return
    if (isNative) {
      native.sendTransaction({
        to: chain.treasury as `0x${string}`,
        value: parseEther(amount),
        chainId: chain.chainId,
      })
      return
    }
    erc20.writeContract({
      address: selected.address as `0x${string}`,
      abi: erc20Abi,
      functionName: 'transfer',
      args: [chain.treasury as `0x${string}`, parseUnits(amount, selected.decimals)],
      chainId: chain.chainId,
    })
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(chain.treasury)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      // Clipboard is blocked in some contexts; the address is on screen anyway.
    }
  }

  return (
    <div>
      <p className="text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
        Send from the wallet you signed in with. Deposits are matched by sender, so funds sent from
        an exchange account cannot be credited automatically.
      </p>

      {wallet.depositChains.length > 1 && (
        <div className="mt-4">
          <div className="eyebrow">Network</div>
          <div className="mt-1.5 flex flex-wrap gap-2" role="group" aria-label="Network">
            {wallet.depositChains.map((c) => {
              const on = c.chainId === chain.chainId
              return (
                <button
                  key={c.chainId}
                  type="button"
                  aria-pressed={on}
                  onClick={() => pickChain(c)}
                  className="border px-3 py-1.5 text-sm font-semibold transition-colors"
                  style={{
                    borderColor: on ? 'var(--color-brand)' : 'var(--color-line)',
                    background: on ? 'var(--color-brand-wash)' : 'var(--color-surface)',
                    color: on ? 'var(--color-brand)' : 'var(--color-muted)',
                  }}
                >
                  {c.chainName}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {chain.assets.length > 1 && (
        <div className="mt-4">
          <div className="eyebrow">Asset</div>
          <div className="mt-1.5 flex flex-wrap gap-2" role="group" aria-label="Asset">
            {chain.assets.map((a) => {
              const on = a.symbol === selected.symbol
              return (
                <button
                  key={a.symbol}
                  type="button"
                  aria-pressed={on}
                  onClick={() => {
                    setSelected(a)
                    setAmount('')
                    reset()
                  }}
                  className="border px-3 py-1.5 text-sm font-semibold transition-colors"
                  style={{
                    borderColor: on ? 'var(--color-brand)' : 'var(--color-line)',
                    background: on ? 'var(--color-brand-wash)' : 'var(--color-surface)',
                    color: on ? 'var(--color-brand)' : 'var(--color-muted)',
                  }}
                >
                  {a.symbol}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* The wallet has to be on the chosen network before anything is sent.
          The treasury is the same address everywhere, so a mismatched send
          would still arrive — on the wrong chain, credited by the wrong
          scanner, after the wrong confirmation wait. */}
      {isConnected && !onRightChain && (
        <div
          className="mt-4 flex flex-wrap items-center justify-between gap-3 border p-3"
          style={{ borderColor: 'var(--color-down)' }}
        >
          <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
            Your wallet is on another network.
          </span>
          <button
            className="btn btn-ghost btn-sm"
            disabled={switching}
            onClick={() => switchChain({ chainId: chain.chainId })}
          >
            {switching ? 'Switching…' : `Switch to ${chain.chainName}`}
          </button>
        </div>
      )}

      <div className="mt-4">
        <label htmlFor="deposit-amount" className="eyebrow">
          Amount
        </label>
        <div className="mt-1.5 flex gap-2">
          <input
            id="deposit-amount"
            className="panel-2 tnum w-full px-3 py-2 text-sm outline-none"
            inputMode="decimal"
            placeholder={selected.pricing === 'priced' ? '0.05' : (wallet.limits.minDepositCents / 100).toFixed(2)}
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value.replace(/[^0-9.]/g, ''))
              reset()
            }}
          />
          <button
            className="btn btn-primary btn-sm shrink-0"
            disabled={!valid || !isConnected || !onRightChain || isPending || receipt.isLoading}
            onClick={send}
          >
            {isPending ? 'Check wallet…' : receipt.isLoading ? 'Confirming…' : 'Send'}
          </button>
        </div>

        {error && (
          <p className="mt-2 text-xs" style={{ color: 'var(--color-down)' }} role="alert">
            {depositErrorMessage(error)}
          </p>
        )}

        {receipt.isSuccess && (
          <p className="mt-2 text-xs" style={{ color: 'var(--color-up)' }}>
            Sent. It will be credited after {chain.confirmations} confirmations on{' '}
            {chain.chainName}, and this page updates on its own.
          </p>
        )}
      </div>

      {/* The manual path, for a hardware wallet or a phone that is not the
          browser session. Same address, same attribution rule. */}
      <div className="mt-5 border-t pt-4">
        <div className="eyebrow">Or send manually</div>
        <div className="mt-2 flex items-center gap-2">
          <code className="panel-2 hash flex-1 px-3 py-2" style={{ color: 'var(--color-text)' }}>
            {chain.treasury}
          </code>
          <button className="btn btn-ghost btn-sm shrink-0" onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <p className="mt-2 text-[0.6875rem] leading-relaxed" style={{ color: 'var(--color-faint)' }}>
          {selected.symbol} on {chain.chainName} only, and only from{' '}
          {address ? shortAddress(address) : 'your signed-in wallet'}. Anything else has to be
          matched up by hand.
        </p>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Withdraw
 * ------------------------------------------------------------------ */

function Withdraw({ wallet }: { wallet: Live }) {
  const [amount, setAmount] = useState('')
  const request = useRequestWithdrawal()

  const amountCents = useMemo(() => {
    const n = Number(amount)
    return Number.isFinite(n) ? Math.round(n * 100) : 0
  }, [amount])

  const ceiling = Math.min(
    wallet.availableCents,
    wallet.limits.maxWithdrawalCents,
    wallet.limits.dailyRemainingCents,
  )
  const valid = amountCents >= wallet.limits.minWithdrawalCents && amountCents <= ceiling
  const needsReview = amountCents > wallet.limits.autoApproveUnderCents

  return (
    <div>
      <p className="text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
        {wallet.token.symbol} is returned to the address you signed in with. There is no destination
        field on purpose: funds can only ever go back where they came from.
      </p>

      <div className="mt-4 flex flex-wrap gap-6">
        <Stat label="Available" value={cents(wallet.availableCents)} />
        <Stat label="Left today" value={cents(wallet.limits.dailyRemainingCents)} />
      </div>

      <div className="mt-4">
        <label htmlFor="withdraw-amount" className="eyebrow">
          Amount
        </label>
        <div className="mt-1.5 flex gap-2">
          <input
            id="withdraw-amount"
            className="panel-2 tnum w-full px-3 py-2 text-sm outline-none"
            inputMode="decimal"
            placeholder={(wallet.limits.minWithdrawalCents / 100).toFixed(2)}
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value.replace(/[^0-9.]/g, ''))
              request.reset()
            }}
          />
          <button
            className="btn btn-ghost btn-sm shrink-0"
            onClick={() => setAmount((ceiling / 100).toFixed(2))}
            disabled={ceiling < wallet.limits.minWithdrawalCents}
          >
            Max
          </button>
          <button
            className="btn btn-primary btn-sm shrink-0"
            disabled={!valid || request.isPending}
            onClick={() =>
              request.mutate(amountCents, { onSuccess: () => setAmount('') })
            }
          >
            {request.isPending ? 'Requesting…' : 'Withdraw'}
          </button>
        </div>

        <div className="mt-2 text-[0.6875rem] leading-relaxed" style={{ color: 'var(--color-faint)' }}>
          To {shortAddress(wallet.withdrawTo)} · {cents(wallet.limits.minWithdrawalCents)}–
          {cents(wallet.limits.maxWithdrawalCents)} per withdrawal
          {needsReview && valid && (
            <span style={{ color: 'var(--color-muted)' }}>
              {' '}
              · over {cents(wallet.limits.autoApproveUnderCents)}, so this one is reviewed by hand
              before it sends
            </span>
          )}
        </div>

        {request.error && (
          <p className="mt-2 text-xs" style={{ color: 'var(--color-down)' }} role="alert">
            {request.error instanceof ApiError
              ? request.error.message
              : 'Could not request that withdrawal.'}
          </p>
        )}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * History
 * ------------------------------------------------------------------ */

const DEPOSIT_LABEL: Record<Exclude<DepositRow['status'], 'seen'>, { text: string; tone: string }> = {
  credited: { text: 'Credited', tone: 'var(--color-up)' },
  unattributed: { text: 'Needs review', tone: 'var(--color-down)' },
  ignored: { text: 'Below minimum', tone: 'var(--color-faint)' },
}

const WITHDRAWAL_LABEL: Record<WithdrawalRow['status'], { text: string; tone: string }> = {
  requested: { text: 'Awaiting review', tone: 'var(--color-muted)' },
  approved: { text: 'Queued', tone: 'var(--color-muted)' },
  signed: { text: 'Signing', tone: 'var(--color-muted)' },
  broadcast: { text: 'Sending', tone: 'var(--color-muted)' },
  confirmed: { text: 'Sent', tone: 'var(--color-up)' },
  failed: { text: 'Failed', tone: 'var(--color-down)' },
  rejected: { text: 'Returned', tone: 'var(--color-faint)' },
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-4 px-4 py-3">{children}</div>
}

function DepositHistory({ rows }: { rows: DepositRow[] }) {
  return (
    <section>
      <h2 className="mb-3 text-base font-semibold">Deposits</h2>
      {rows.length === 0 ? (
        <EmptyState title="No deposits yet" body="Anything you send shows up here within a minute." />
      ) : (
        <div className="panel divide-y overflow-hidden">
          {rows.map((d) => {
            if (d.status === 'seen') {
              // No terminal label yet — a live confirmation count instead, so
              // "nothing happened" and "12 blocks left to wait" don't look the
              // same while a real deposit is genuinely on its way in.
              const required = d.confirmationsRequired || 1
              const pct = Math.min(100, Math.round((d.confirmations / required) * 100))
              return (
                <Row key={d.id}>
                  <div className="min-w-0 flex-1">
                    <div className="tnum text-sm font-semibold">
                      {cents(d.amountCents)} <span style={{ color: 'var(--color-faint)' }}>{d.asset}</span>
                    </div>
                    <div className="hash truncate">{d.txHash}</div>
                  </div>
                  <div className="w-28 shrink-0 text-right">
                    <div className="tnum text-xs font-semibold" style={{ color: 'var(--color-muted)' }}>
                      {d.confirmations}/{d.confirmationsRequired} confirmations
                    </div>
                    <div
                      className="mt-1.5 ml-auto h-1 w-full overflow-hidden rounded-full"
                      style={{ background: 'var(--color-surface-3)' }}
                    >
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, background: 'var(--color-brand)' }}
                      />
                    </div>
                  </div>
                </Row>
              )
            }

            const label = DEPOSIT_LABEL[d.status]
            return (
              <Row key={d.id}>
                <div className="min-w-0 flex-1">
                  <div className="tnum text-sm font-semibold">
                    {cents(d.amountCents)} <span style={{ color: 'var(--color-faint)' }}>{d.asset}</span>
                  </div>
                  <div className="hash truncate">{d.txHash}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-semibold" style={{ color: label.tone }}>
                    {label.text}
                  </div>
                  <div className="text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
                    {timeAgo(d.creditedAt ?? d.seenAt)}
                  </div>
                </div>
              </Row>
            )
          })}
        </div>
      )}
    </section>
  )
}

function WithdrawalHistory({ rows }: { rows: WithdrawalRow[] }) {
  const cancel = useCancelWithdrawal()

  return (
    <section>
      <h2 className="mb-3 text-base font-semibold">Withdrawals</h2>
      {rows.length === 0 ? (
        <EmptyState title="No withdrawals yet" body="Requests and their progress are listed here." />
      ) : (
        <div className="panel divide-y overflow-hidden">
          {rows.map((w) => {
            const label = WITHDRAWAL_LABEL[w.status]
            return (
              <Row key={w.id}>
                <div className="min-w-0 flex-1">
                  <div className="tnum text-sm font-semibold">{cents(w.amountCents)}</div>
                  <div className="truncate text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
                    {w.txHash ? w.txHash : `to ${shortAddress(w.toAddress)}`}
                  </div>
                  {w.failure && (
                    <div className="mt-0.5 text-[0.6875rem]" style={{ color: 'var(--color-down)' }}>
                      {w.failure}
                    </div>
                  )}
                </div>

                <div className="shrink-0 text-right">
                  <div className="text-xs font-semibold" style={{ color: label.tone }}>
                    {label.text}
                  </div>
                  <div className="text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
                    {timeAgo(w.confirmedAt ?? w.requestedAt)}
                  </div>
                </div>

                {w.cancellable && (
                  <button
                    className="btn btn-quiet btn-sm shrink-0"
                    disabled={cancel.isPending}
                    onClick={() => cancel.mutate(w.id)}
                  >
                    Cancel
                  </button>
                )}
              </Row>
            )
          })}
        </div>
      )}
    </section>
  )
}
